import { Link } from "react-router-dom";
import { Bell, CalendarDays, Briefcase, Clock, MapPin, Inbox, FileText, ChevronRight } from "lucide-react";
import { useNotificaciones } from "@/hooks/useNotificaciones";
import { fmtFecha, fmtHora } from "@/lib/format";
import { Badge } from "@/components/ui/badge";

const TIPO_ICON = {
  fichaje_recordatorio: Clock,
  entrada_temprana_patron: Clock,
  ausencia_justificacion: CalendarDays,
  compensacion: Briefcase,
  horario_cambio: Clock,
  zona_trabajo: MapPin,
  solicitud_confirmacion: Inbox,
  solicitud_revisada: CalendarDays,
  intercambio_pareja: CalendarDays,
  intercambio: CalendarDays,
  documento: FileText,
  proyecto: Briefcase,
};

const TIPO_ACCENT = {
  fichaje_recordatorio: "border-amber-200/90 bg-amber-50/90",
  entrada_temprana_patron: "border-orange-200/90 bg-orange-50/90",
  ausencia_justificacion: "border-rose-200 bg-rose-50/70",
  compensacion: "border-violet-200 bg-violet-50/70",
  horario_cambio: "border-sky-200 bg-sky-50/70",
  zona_trabajo: "border-fuchsia-200 bg-fuchsia-50/70",
  solicitud_confirmacion: "border-indigo-200 bg-indigo-50/80",
  solicitud_revisada: "border-emerald-200 bg-emerald-50/70",
  intercambio_pareja: "border-orange-200 bg-orange-50/70",
  intercambio: "border-orange-200 bg-orange-50/70",
  documento: "border-slate-200 bg-slate-50/70",
  proyecto: "border-violet-200 bg-violet-50/70",
};

const MAX_VISIBLES = 10;

export default function NotificacionesPendientesSimple() {
  const { items, total_pendientes } = useNotificaciones(45000, "trabajador");
  const pendientes = (items || []).filter((i) => i.pendiente_accion);

  if (!total_pendientes || pendientes.length === 0) {
    return null;
  }

  const visibles = pendientes.slice(0, MAX_VISIBLES);
  const hayMas = total_pendientes > visibles.length;

  return (
    <section className="space-y-2" data-testid="notificaciones-pendientes-fichar">
      {visibles.map((item) => {
        const Icon = TIPO_ICON[item.tipo] || Bell;
        const accent = TIPO_ACCENT[item.tipo] || "border-zinc-200 bg-white";
        return (
          <Link
            key={item.id}
            to="/mis-notificaciones"
            className={`block rounded-xl border p-3 shadow-sm transition-colors hover:shadow-md active:scale-[0.99] ${accent}`}
            data-testid={`notif-pendiente-${item.id}`}
          >
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                <Icon className="h-4 w-4 text-indigo-600" strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide bg-white/80">
                      {item.categoria}
                    </Badge>
                    <Badge className="bg-amber-500 text-[10px] hover:bg-amber-500">Pendiente</Badge>
                  </div>
                  <time className="shrink-0 text-[10px] text-zinc-500 whitespace-nowrap">
                    {fmtFecha(item.created_at)} · {fmtHora(item.created_at)}
                  </time>
                </div>
                <p className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-zinc-900">
                  {item.titulo}
                </p>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-zinc-400" />
            </div>
          </Link>
        );
      })}

      {hayMas && (
        <Link
          to="/mis-notificaciones"
          className="flex items-center justify-center gap-1 rounded-xl border border-indigo-100 bg-white/80 py-2.5 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-50"
          data-testid="notificaciones-ver-todas"
        >
          Ver todos los avisos ({total_pendientes})
          <ChevronRight className="h-4 w-4" />
        </Link>
      )}
    </section>
  );
}
